/**
 * "What's next" beat between phases. Eva sees Pumpkin, a short
 * explanation of the upcoming phase, and a "ready" button she taps to
 * proceed at her own pace.
 *
 * Why a button (not a 2s auto-advance, per the spec §4.1 "ready?"
 * mention): for a 6yo, reading two short sentences takes longer than 2s,
 * and Eva benefits from agency over the pace. The intro also speaks
 * itself aloud on mount so non-reading moments still carry the context.
 *
 * Pumpkin's presence here matches spec §7's "visible on... inter-phase
 * transitions" — she vanishes again on each task-focus screen.
 *
 * Three kinds, mirroring the three phase handoffs we wanted to soften:
 *   - "firstPass"     after flash card → before first-pass spelling
 *   - "secondChance"  after results "Let's try again" → before retry
 *   - "threeXWrite"   after second-chance ends → before paper write
 *
 * Pose mapping:
 *   - firstPass     → waveHello       (greeting, kicking off the test)
 *   - secondChance  → gentleEncourage (CLAUDE.md mapping for retry prompt)
 *   - threeXWrite   → waveHello       (welcoming her into the paper task)
 *
 * If somehow there's no session (deep-link, refresh), we bounce home
 * rather than render an intro to nothing — same defensive pattern as
 * the gameplay screens.
 */
import { useEffect } from "react";

import type { PumpkinPose } from "@/assets/pumpkin-images";
import { Button } from "@/components/Button";
import { PumpkinFigure } from "@/components/PumpkinFigure";
import { useNavigation } from "@/context/NavigationContext";
import type { AppScreen } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { playTransition } from "@/lib/sounds";
import { cancelSpeech, speak } from "@/lib/speech";
import "./PhaseIntroScreen.css";

export type PhaseIntroKind = "firstPass" | "secondChance" | "threeXWrite";

interface IntroContent {
  title: string;
  body: string;
  /** Audio version — kept short; iOS Safari is happier with concise utterances. */
  spoken: string;
  buttonLabel: string;
  pose: PumpkinPose;
  alt: string;
  next: AppScreen;
}

const CONTENT: Record<PhaseIntroKind, IntroContent> = {
  firstPass: {
    title: "Time to spell!",
    body: "I'll say each word. Type it as best you can, then tap Done.",
    spoken: "Time to spell! I'll say each word. Type it as best you can.",
    buttonLabel: "I'm ready!",
    pose: "waveHello",
    alt: "Pumpkin the pangolin, waving hello",
    next: "firstPass",
  },
  secondChance: {
    title: "Let's try those again",
    body: "Listen carefully — I'll say each tricky word one more time.",
    spoken: "Let's try those again! Listen carefully.",
    buttonLabel: "OK!",
    pose: "gentleEncourage",
    alt: "Pumpkin the pangolin, encouraging",
    next: "secondChance",
  },
  threeXWrite: {
    title: "Time to write!",
    body: "Make sure you have a pencil and paper ready.",
    spoken: "Time to write! Make sure you have a pencil and paper ready.",
    buttonLabel: "Got it!",
    pose: "waveHello",
    alt: "Pumpkin the pangolin, waving",
    next: "threeXWrite",
  },
};

export function PhaseIntroScreen({ kind }: { kind: PhaseIntroKind }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const content = CONTENT[kind];

  // Bounce home if there's no session (deep-link / refresh during play).
  useEffect(() => {
    if (!session) navigate("home");
  }, [session, navigate]);

  // Speak the intro line on mount. Kept in its own effect so the
  // navigate-home check above can fire without re-running speech.
  useEffect(() => {
    if (!session) return;
    // Paper-rustle transition cue (design.md §8) — plays before speech
    // so the handoff feels "thumb-page-turn, then Pumpkin talks". The
    // synth is short (~300ms) so the speech still starts immediately.
    void playTransition();
    speak(content.spoken, session.settings);
    return () => cancelSpeech();
    // Re-fire when the spoken text changes (i.e. a different intro
    // screen mounts), not on session-object identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.spoken]);

  return (
    <main className="phase-intro">
      <div className="phase-intro__inner">
        <PumpkinFigure
          className="phase-intro__pumpkin pumpkin-idle"
          pose={content.pose}
          alt={content.alt}
        />
        <h1 className="phase-intro__title">{content.title}</h1>
        <p className="phase-intro__body">{content.body}</p>
        <Button size="large" onClick={() => navigate(content.next)}>
          {content.buttonLabel}
        </Button>
      </div>
    </main>
  );
}
